<template>
	<div class="fd-view-columns" v-if="visible" :class="clazz" :style="style">
		<column-list :source="false" :list="visibleColumns" @coldrop="addColumn"></column-list>
		<sym-icon name="drag-between" style="font-size:28px"></sym-icon>
		<column-list :source="true" :list="availableColumns" @coldrop="removeColumn"></column-list>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, watch} from "vue";
import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor.ts";
import type {ViewColumns} from "@t/fd/editor/ViewColumns.ts";
import {injectEnv} from "@fd/Environment.ts";
import {useAgendaStore} from "@/store/AgendaStore.ts";
import type {ColumnListItem} from "@t/agenda/Columns.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import ColumnList from "@fd/editors/view/ColumnList.vue";

const uprops = defineProps({
	...editorProps
});
const props = uprops as EditorProps;
const vc = computed<ViewColumns>(() => props.part as ViewColumns);
const {visible, value, clazz, style, ro, vm} = useEditor(props);
const env = injectEnv();
const ast = useAgendaStore();

const allColumns = ref<ColumnListItem[]>([]);

watch(() => vc.value.descriptor, (desc) => {
	ast.getVisibleColumns(desc).then((cols) => {
		allColumns.value.length = 0;
		allColumns.value.push(...cols);
	});
}, { immediate: true, deep: true });

const visibleColumns = computed<ColumnListItem[]>(() => {
	if (!Array.isArray(value.value?.columns)) return [];
	const result:ColumnListItem[] = [];
	for (let colName of value.value.columns) {
		for (let col of allColumns.value) {
			if (colName === col.value) result.push(col);
		}
	}
	return result;
});

const availableColumns = computed<ColumnListItem[]>(() => {
	if (!Array.isArray(value.value?.columns)) return allColumns.value;
	const result:ColumnListItem[] = [];
	for (let col of allColumns.value) {
		let isVisible = false;
		for (let colName of value.value.columns) {
			if (colName === col.value) {
				isVisible = true;
				break;
			}
		}
		if (!isVisible) result.push(col);
	}
	return result;
});

function addColumn(name: string, index: number) {
	console.log("Adding column " + name + " @ " + index);
	if (!Array.isArray(value.value?.columns)) {
		// make it array
		value.value.columns = [ name ];
		return;
	}
	const currentIdx = value.value.columns.indexOf(name);
	if (index < 0) index = 0;
	if (currentIdx < 0) {
		// Not there (this is move from available columns)
		if (index > value.value.columns.length) value.value.columns.push(name);
		else value.value.columns.splice(index, 0, name);
	} else {
		// Already there (this is local reorder)
		if (index === currentIdx || index === (currentIdx+1)) return;	// do noting (stay in the same place)
		value.value.columns.splice(currentIdx, 1);	// remove from current position
		if (index < currentIdx) {
			// Dropping before current position (no need to compensate)
			value.value.columns.splice(index, 0, name);
		} else {
			// Dropping after curent position
			value.value.columns.splice(index-1, 0, name);
		}
	}
}

function removeColumn(name: string) {
	console.log("Removing column " + name);
	if (!Array.isArray(value.value?.columns)) {
		value.value.columns = [];
		return;
	}
	const idx = value.value.columns.indexOf(name);
	if (idx >= 0) value.value.columns.splice(idx, 1);
}
</script>

<style>
.fd-view-columns {
	display: grid;
	grid-template-columns: 1fr 36px 1fr;
	align-items: center;
	column-gap: 8px;
}
.view-columns {
	min-height: 20em;
	min-width: 14em;
	overflow-y: auto;
	padding: 8px;
	display: flex;
	flex-direction: column;
	row-gap: 4px;
}
.fd-view-column {
	background-color: var(--pill-bg);
	color: var(--pill-fg);
	padding: 2px 8px;
	line-height: var(--input-height);
	border-radius: var(--button-radius);
	cursor: move;
}
</style>